import React from "react";

import {
  faBirthdayCake,
  faCow,
  faFileCertificate,
  faWeight,
} from "@fortawesome/pro-solid-svg-icons";
import { Grid } from "@material-ui/core";
import intersection from "lodash/intersection";
import { useSelector } from "react-redux";
import styled from "styled-components/macro";

import {
  CheckBox,
  Input,
  Label,
  MutuallyExclusiveInput,
  OptionTogglerField,
  TextArea,
  withNamespace,
} from "components/Form/FormikControls";
import { Tooltip } from "components/Form/FormikControls/Tooltip";
import { Row } from "components/Layout";
import { GridFlexDown } from "components/Layout/GridFlexDown";
import { Paper } from "components/Paper";
import { useResponsiveText } from "components/ResponsiveText";
import { MediumText as Heading } from "components/Text";

import {
  Accreditation,
  FrameStructure,
  VendorBredOptions,
} from "constants/draftingAttributes";
import { ExportSite } from "constants/exportSites";

import {
  diffBreedHdAllocations,
  diffDentitionHdAllocations,
  diffFatScoreHdAllocations,
  diffHornHdAllocations,
  diffMuscleScoreHdAllocations,
} from "lib/validation/allocation";

import { selectAreStudFeaturesEnabled } from "selectors";

import { BreedSection } from "./Breed";
import { Error, Icon, To } from "./components";
import { DentitionSection } from "./Dentition";
import { FatScoreSection } from "./FatScore";
import { HornSection } from "./Horn";
import { IsNeeAccreditedField } from "./IsNeeAccreditedField";
import { MuscleScoreSection } from "./MuscleScore";
import {
  TimeUnitChoices,
  allocationDiffErrorMessage,
  withAttributeNamespace,
} from "./util";
import { VisibilitySection } from "./Visibility";

const TooltipHeading = styled(Heading)`
  margin-right: 5px;
`;

interface NamespaceReadOnlyProps {
  namespace?: string;
  readOnly?: boolean;
}

interface ErrorMessageProps extends NamespaceReadOnlyProps {
  errorMessage?: React.JSX.Element | null;
}

interface CattleAttributeConfig {
  fieldName: string;
  label: string;
  tooltip?: string;
  trueValue: Accreditation;
  falseValue: Accreditation;
  mutuallyExclusiveWithName: string | null;
  mutuallyExclusiveWithFalseValue: Accreditation | null;
}

const AgeSection = ({
  namespace,
  readOnly,
}: NamespaceReadOnlyProps): React.JSX.Element => {
  return (
    <Grid item xs={12}>
      <Paper>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Icon icon={faBirthdayCake} color="gray44" />
            <Heading>Age Range</Heading>
          </Grid>
          <Grid item xs={6}>
            <Row>
              <Input
                label=""
                name={withAttributeNamespace("minAge", namespace)}
                type="number"
                emptyValue={null}
                disabled={readOnly}
              />
              <To>To</To>
              <Input
                label=""
                name={withAttributeNamespace("maxAge", namespace)}
                type="number"
                emptyValue={null}
                disabled={readOnly}
              />
            </Row>
          </Grid>

          <Grid item xs={6}>
            <OptionTogglerField
              options={TimeUnitChoices}
              name={withAttributeNamespace("ageRangeTimeUnit", namespace)}
              disabled={readOnly}
            />
          </Grid>

          <Grid item xs={12}>
            <CheckBox
              label="Weaned (weaners only)"
              name={withAttributeNamespace("isWeaned", namespace)}
              disabled={readOnly}
            />
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
};

const FrameSection = ({
  errorMessage,
  namespace,
  readOnly,
}: ErrorMessageProps): React.JSX.Element => (
  <Grid item xs={12}>
    <Paper>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Row flexGrow>
            <Icon icon={faCow} color="gray44" />
            <Heading>Frame Structure</Heading>
            {errorMessage && <Error>{errorMessage}</Error>}
          </Row>
        </Grid>
        <Grid item xs={12}>
          <OptionTogglerField
            name={withAttributeNamespace("frameStructure", namespace)}
            options={Object.values(FrameStructure)}
            allowToggleOff
            disabled={readOnly}
            missingMLAFieldsWarningEnabled
            overrideMissingFieldName="frameStructure"
            label="Frame Structure"
            labelPosition="top"
          />
        </Grid>
      </Grid>
    </Paper>
  </Grid>
);

export const cattleAttributes: CattleAttributeConfig[] = [
  {
    fieldName: "accreditationEU",
    label: "EU Eligible",
    tooltip:
      "European Union Cattle Accreditation Scheme. This value is prefilled from the EU status on the Vendor's PIC",
    trueValue: Accreditation.ALL,
    falseValue: Accreditation.NONE,
    mutuallyExclusiveWithName: null,
    mutuallyExclusiveWithFalseValue: null,
  },
  {
    fieldName: "accreditationPTIC",
    label: "PTIC",
    tooltip: "Refer to Additional Information on NVD",
    trueValue: Accreditation.ALL,
    falseValue: Accreditation.NONE,
    mutuallyExclusiveWithName: "accreditationPTE",
    mutuallyExclusiveWithFalseValue: Accreditation.NONE,
  },
  {
    fieldName: "accreditationPTE",
    label: "PTE",
    tooltip: "Refer to Additional Information on NVD",
    trueValue: Accreditation.ALL,
    falseValue: Accreditation.NONE,
    mutuallyExclusiveWithName: "accreditationPTIC",
    mutuallyExclusiveWithFalseValue: Accreditation.NONE,
  },
  {
    fieldName: "accreditationPCAS",
    label: "PCAS",
    tooltip:
      "Pasturefed Cattle Assurance System. This value is prefilled from the PCAS status on the Vendor's PIC",
    trueValue: Accreditation.ALL,
    falseValue: Accreditation.NONE,
    mutuallyExclusiveWithName: null,
    mutuallyExclusiveWithFalseValue: null,
  },
  {
    fieldName: "accreditationAntibioticFree",
    label: "Antibiotic Free",
    trueValue: Accreditation.ALL,
    falseValue: Accreditation.NONE,
    mutuallyExclusiveWithName: null,
    mutuallyExclusiveWithFalseValue: null,
  },
  {
    fieldName: "accreditationOrganic",
    label: "Organic",
    tooltip: "Refer to Additional Information on NVD",
    trueValue: Accreditation.ALL,
    falseValue: Accreditation.NONE,
    mutuallyExclusiveWithName: null,
    mutuallyExclusiveWithFalseValue: null,
  },
  {
    fieldName: "stationMated",
    label: "Station Mated",
    tooltip: "Refer to Additional Information on NVD",
    trueValue: Accreditation.ALL,
    falseValue: Accreditation.NONE,
    mutuallyExclusiveWithName: "notStationMated",
    mutuallyExclusiveWithFalseValue: Accreditation.NONE,
  },
  {
    fieldName: "notStationMated",
    label: "Not Station Mated",
    tooltip: "Refer to Additional Information on NVD",
    trueValue: Accreditation.ALL,
    falseValue: Accreditation.NONE,
    mutuallyExclusiveWithName: "stationMated",
    mutuallyExclusiveWithFalseValue: Accreditation.NONE,
  },
  {
    fieldName: "accreditationBackgrounded",
    label: "Backgrounded",
    tooltip: "Refer to Additional Information on NVD",
    trueValue: Accreditation.ALL,
    falseValue: Accreditation.NONE,
    mutuallyExclusiveWithName: "feeder",
    mutuallyExclusiveWithFalseValue: Accreditation.NONE,
  },
  {
    fieldName: "feeder",
    label: "Feeder",
    tooltip: "Agent Assessed as a Feeder",
    trueValue: Accreditation.ALL,
    falseValue: Accreditation.NONE,
    mutuallyExclusiveWithName: "accreditationBackgrounded",
    mutuallyExclusiveWithFalseValue: Accreditation.NONE,
  },
];

interface AttributesSectionProps extends ErrorMessageProps {
  consignmentId: string;
}

const AttributesSection = ({
  consignmentId,
  errorMessage,
  namespace,
  readOnly,
}: AttributesSectionProps): React.JSX.Element => {
  const areStudFeaturesEnabled = useSelector(selectAreStudFeaturesEnabled);
  return (
    <Grid item xs={12}>
      <Paper>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Icon icon={faFileCertificate} color="gray44" />
            <Heading>Attributes</Heading>
            {errorMessage && <Error>{errorMessage}</Error>}
          </Grid>

          {cattleAttributes.map(attribute => (
            <Grid item xs={6} sm={4} key={attribute.fieldName}>
              <CheckBox
                name={withAttributeNamespace(attribute.fieldName, namespace)}
                label={attribute.label}
                tooltip={attribute.tooltip}
                trueValue={attribute.trueValue}
                falseValue={attribute.falseValue}
                mutuallyExclusiveWithName={
                  attribute.mutuallyExclusiveWithName
                    ? withAttributeNamespace(
                        attribute.mutuallyExclusiveWithName,
                        namespace,
                      )
                    : null
                }
                mutuallyExclusiveWithFalseValue={
                  attribute.mutuallyExclusiveWithFalseValue
                }
                disabled={readOnly}
              />
            </Grid>
          ))}
          <Grid item xs={2} sm={1} container alignItems="center">
            <Label>JBAS</Label>
          </Grid>
          <Grid item xs={4} sm={3}>
            <Row alignCenter>
              <Input
                name={withAttributeNamespace("accreditationJBAS", namespace)}
                maxLength={1}
                type="number"
                width="50%"
                columnProps={{ fullWidth: true, alignEnd: true }}
                disabled={readOnly}
              />
              &nbsp;
              <Tooltip title="This value is prefilled from the JBAS status on the Vendor's PIC" />
            </Row>
          </Grid>

          <Grid item xs={6} sm={4}>
            <CheckBox
              name={withAttributeNamespace("accreditationGrassFed", namespace)}
              label="Grass Fed"
              trueValue={Accreditation.ALL}
              falseValue={Accreditation.NONE}
              disabled={readOnly}
              mutuallyExclusiveWithName={withAttributeNamespace(
                "grainFedDays",
                namespace,
              )}
              mutuallyExclusiveWithFalseValue={0}
            />
          </Grid>
          <Grid item xs={2} sm={1} container alignItems="center">
            <Label>Grain Fed</Label>
          </Grid>
          <Grid item xs={4} sm={3}>
            <Row alignCenter>
              <MutuallyExclusiveInput
                name={withAttributeNamespace("grainFedDays", namespace)}
                maxLength={4}
                type="number"
                width="50%"
                columnProps={{ fullWidth: true, alignEnd: true }}
                disabled={readOnly}
                mutuallyExclusiveWithName={withAttributeNamespace(
                  "accreditationGrassFed",
                  namespace,
                )}
                mutuallyExclusiveWithFalseValue={Accreditation.NONE}
              />
              &nbsp;
              <Tooltip title="Number of Days on Feed" />
            </Row>
          </Grid>
        </Grid>
      </Paper>

      <Paper>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12}>
            <IsNeeAccreditedField
              consignmentId={consignmentId}
              readOnly={readOnly}
              namespace={namespace}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper>
        <Grid item xs={12}>
          <TextArea
            label="Comments (Placard Text)"
            name={withAttributeNamespace("publicDescription", namespace)}
            tooltip="Any other details you wish to provide."
            disabled={readOnly}
          />
        </Grid>
        <Grid item xs={12}>
          <TextArea
            label="Fault Description"
            name={withAttributeNamespace("publicFaultDescription", namespace)}
            tooltip="Anything you would declare in an assessment."
            disabled={readOnly}
          />
        </Grid>
        <Grid item xs={12}>
          <Input
            label="Pen may be sold as part of a run"
            name={withAttributeNamespace("partOfRun", namespace)}
            tooltip="Other pens that may be sold as part of a run."
            disabled={readOnly}
          />
        </Grid>
        {!areStudFeaturesEnabled && (
          <Grid item xs={12}>
            <Input
              label="Tag #"
              name={withAttributeNamespace("tagNumber", namespace)}
              disabled={readOnly}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={12}>
          <OptionTogglerField
            label="Vendor Bred"
            labelPosition="top"
            name={withAttributeNamespace("vendorBredOverride", namespace)}
            options={VendorBredOptions}
            disabled={readOnly}
          />
        </Grid>
      </Paper>
    </Grid>
  );
};

interface CattleBreedSectionProps extends ErrorMessageProps {
  showFullDrafting?: boolean;
  onChangeBreed?: (value: string | number | null) => void;
}

const CattleBreedSection = ({
  errorMessage,
  showFullDrafting,
  onChangeBreed,
  namespace,
  readOnly,
}: CattleBreedSectionProps): React.JSX.Element => (
  <BreedSection
    errorMessage={errorMessage}
    showFullDrafting={showFullDrafting}
    onChangeBreed={onChangeBreed}
    namespace={namespace}
    readOnly={readOnly}
  >
    <Grid item xs={12}>
      <Input
        label="Bloodline"
        name={withAttributeNamespace("bloodline", namespace)}
        tooltip="Breeding information."
        disabled={readOnly}
      />
    </Grid>
  </BreedSection>
);

// Shared component for the 3 key MLA weight fields (cattle)
const CattleMLAWeightFields = ({
  namespace,
  readOnly,
}: NamespaceReadOnlyProps): React.JSX.Element => {
  const estCarcassWeightLabel = useResponsiveText({
    mobile: "Cwt",
    tablet: "Est Carcass Weight",
    desktop: "Est Carcass Weight",
  });
  const estLiveWeightLabel = useResponsiveText({
    mobile: "Lwt",
    tablet: "Est Live Weight",
    desktop: "Est Live Weight",
  });
  const dressingPercentLabel = useResponsiveText({
    mobile: "Dressing %",
    tablet: "Dressing Percent",
    desktop: "Dressing Percent",
  });

  return (
    <>
      <GridFlexDown>
        <Input
          label={estCarcassWeightLabel}
          name={withAttributeNamespace("estCarcassWeight", namespace)}
          type="number"
          afterSymbol="Kg"
          multiplier={1000}
          decimalPlaces={2}
          decimal
          emptyValue={null}
          tooltip="Estimated Carcass Weight"
          disabled={readOnly}
          missingMLAFieldsWarningEnabled
          overrideMissingFieldName="estCarcassWeight"
          hideMissingFieldLabel
        />
      </GridFlexDown>
      <GridFlexDown>
        <Input
          label={estLiveWeightLabel}
          name={withNamespace(namespace, "estimatedAverageMassGrams")}
          type="number"
          afterSymbol="Kg"
          multiplier={1000}
          decimalPlaces={3}
          decimal
          emptyValue={0}
          tooltip="Estimated Live Weight"
          disabled={readOnly}
          missingMLAFieldsWarningEnabled
          overrideMissingFieldName="estimatedAverageMassGrams"
          hideMissingFieldLabel
        />
      </GridFlexDown>
      <GridFlexDown>
        <Input
          label={dressingPercentLabel}
          name={withAttributeNamespace("dressingPercent", namespace)}
          type="number"
          emptyValue={null}
          afterSymbol="%"
          disabled={readOnly}
          missingMLAFieldsWarningEnabled
          overrideMissingFieldName="dressingPercent"
          hideMissingFieldLabel
        />
      </GridFlexDown>
    </>
  );
};

// Simplified version for general section - only shows the 3 key MLA fields
export const CattleWeightSectionSimple = ({
  namespace,
  readOnly,
}: NamespaceReadOnlyProps): React.JSX.Element => {
  return <CattleMLAWeightFields namespace={namespace} readOnly={readOnly} />;
};

// Full version for detailed section - includes Paper wrapper and heading
export const CattleWeightSection = ({
  namespace,
  readOnly,
}: NamespaceReadOnlyProps): React.JSX.Element => {
  return (
    <Grid item xs={12}>
      <Paper>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Icon icon={faWeight} color="gray44" />
            <TooltipHeading>Weight</TooltipHeading>
            <Tooltip title="Declare the weight, location and accuracy of your weighing" />
          </Grid>
          <CattleMLAWeightFields namespace={namespace} readOnly={readOnly} />
        </Grid>
      </Paper>
    </Grid>
  );
};

function isVisibleFor(
  visibleExportSites: ExportSite[],
  selectedExportSites: ExportSite[] = [],
): boolean {
  return (
    selectedExportSites.length === 0 ||
    intersection(selectedExportSites, visibleExportSites).length > 0
  );
}

interface CattleProps extends NamespaceReadOnlyProps {
  saleLot: Partial<SaleLot>;
  onChangeBreed?: (value: string | number | null) => void;
}

export const Cattle = ({
  saleLot,
  onChangeBreed,
  namespace,
  readOnly,
}: CattleProps): React.JSX.Element => {
  const allocations = {
    fatScores: diffFatScoreHdAllocations(saleLot),
    muscleScores: diffMuscleScoreHdAllocations(saleLot),
    dentition: diffDentitionHdAllocations(saleLot),
    breed: diffBreedHdAllocations(saleLot),
    horn: diffHornHdAllocations(saleLot),
  };
  // If every site is stock live or angus aus we want to show a minimal drafting view
  // else we want to show the full view
  const exportSites: ExportSite[] = saleLot.exportSites || [];

  const showFor = (visibleExportSites: ExportSite[]) =>
    isVisibleFor(visibleExportSites, exportSites);

  return (
    <>
      <VisibilitySection
        consignmentId={saleLot.consignment_id}
        namespace={namespace}
        readOnly={readOnly}
      />
      {showFor([
        ExportSite.AGRINOUS,
        ExportSite.ANGUS_AUSTRALIA,
        ExportSite.AUCTIONS_PLUS,
        ExportSite.LIST_2_SELL,
        ExportSite.STOCK_LIVE,
      ]) && (
        <CattleBreedSection
          errorMessage={allocationDiffErrorMessage(allocations.breed)}
          showFullDrafting={showFor([
            ExportSite.AGRINOUS,
            ExportSite.AUCTIONS_PLUS,
            ExportSite.LIST_2_SELL,
          ])}
          onChangeBreed={onChangeBreed}
          namespace={namespace}
          readOnly={readOnly}
        />
      )}
      {showFor([
        ExportSite.AUCTIONS_PLUS,
        ExportSite.STOCK_LIVE,
        ExportSite.AGRINOUS,
        ExportSite.LIST_2_SELL,
      ]) && <AgeSection namespace={namespace} readOnly={readOnly} />}
      {showFor([
        ExportSite.AUCTIONS_PLUS,
        ExportSite.AGRINOUS,
        ExportSite.LIST_2_SELL,
      ]) && (
        <DentitionSection
          errorMessage={allocationDiffErrorMessage(allocations.dentition)}
          namespace={namespace}
          readOnly={readOnly}
        />
      )}
      <FatScoreSection
        errorMessage={allocationDiffErrorMessage(allocations.fatScores)}
        maxScore={6}
        namespace={namespace}
        readOnly={readOnly}
      />
      <MuscleScoreSection
        errorMessage={allocationDiffErrorMessage(allocations.muscleScores)}
        maxScore={5}
        namespace={namespace}
        readOnly={readOnly}
      />
      {showFor([
        ExportSite.AUCTIONS_PLUS,
        ExportSite.AGRINOUS,
        ExportSite.LIST_2_SELL,
      ]) && <FrameSection namespace={namespace} readOnly={readOnly} />}
      {showFor([
        ExportSite.AUCTIONS_PLUS,
        ExportSite.STOCK_LIVE,
        ExportSite.AGRINOUS,
        ExportSite.LIST_2_SELL,
      ]) && (
        <HornSection
          errorMessage={allocationDiffErrorMessage(allocations.horn)}
          quantity={saleLot.quantity}
          onlyHornedOrPolled={false}
          namespace={namespace}
          readOnly={readOnly}
        />
      )}
      {showFor([
        ExportSite.AUCTIONS_PLUS,
        ExportSite.AGRINOUS,
        ExportSite.LIST_2_SELL,
      ]) && <CattleWeightSection namespace={namespace} readOnly={readOnly} />}
      {showFor([
        ExportSite.AUCTIONS_PLUS,
        ExportSite.STOCK_LIVE,
        ExportSite.AGRINOUS,
        ExportSite.LIST_2_SELL,
      ]) && (
        <AttributesSection
          consignmentId={saleLot.consignment_id}
          namespace={namespace}
          readOnly={readOnly}
        />
      )}
    </>
  );
};
